// =====================================================================
// Demandas — os tokens visuais do módulo
// =====================================================================
// Fonte ÚNICA das cores. O quadro e a página de detalhe leem daqui para não
// divergirem — foi assim que, em outros módulos, a mesma etiqueta acabou verde
// numa tela e cinza na outra.
//
// ⚠️ A cor de IDENTIDADE do módulo é o azul-petróleo `#156082` (registrada em
//    ARQUITETURA.md › Paleta dos módulos). As cores de STATUS abaixo são outra
//    coisa: elas são SEMÂNTICAS (o que aquele estado significa), e por isso
//    podem sair do matiz do módulo sem quebrar a regra da paleta — que fala da
//    identidade do módulo, não do vocabulário de estado.
// =====================================================================

export const COR = '#156082';
export const COR_ESCURA = '#0f435b';
export const COR_CLARA = '#cbe4f0';
// Fundo suave na cor do módulo, para realçar linha sem gritar (ex.: a tarefa já
// concluída na lista de detalhe). Trocou o lima-claro `#f7fee7` quando o módulo
// virou azul-petróleo.
export const COR_FUNDO = '#e8f3f8';

// ⚠️ A BORDA NÃO É A COR DO MÓDULO. Era `#ecfccb` (lima clarinho) e sumia no
//    fundo branco — o card não tinha delimitação visível. Agora é um cinza de
//    verdade: quem separa card de fundo é contraste, não identidade.
export const BORDA = '#cbd5e1';
export const BORDA_SUAVE = '#e2e8f0';

/**
 * Cada estado tem sua cor, seu fundo e seu rótulo.
 *
 * `cor` é o forte (título da coluna, barra lateral do card, borda do botão);
 * `fundo` é o claro, e serve só para SELO — nunca para a coluna (ver
 * `FUNDO_COLUNA` abaixo). Os dois vêm em par de propósito: cor escolhida
 * sozinha vira texto ilegível sobre fundo próprio.
 *
 * ⚠️ As cores são SEMÂNTICAS e foram escolhidas pelo Rodrigo em 17/09:
 *    laranja = precisa de decisão · cinza = na fila · azul = em movimento ·
 *    vermelho = travado · verde = terminou bem.
 *    ⚠️ "Fazendo" NÃO usa a lima do módulo de propósito: a identidade do módulo
 *    vive no cabeçalho e no gradiente; aqui o que fala é o significado do
 *    estado, e lima ao lado de verde-concluído seriam a mesma mancha de longe.
 */
export const STATUS = {
    'Pendente de triagem': { cor: '#b45309', fundo: '#fffbeb', rotulo: 'Pendente de triagem' },
    'A fazer':             { cor: '#475569', fundo: '#f8fafc', rotulo: 'A fazer' },
    'Fazendo':             { cor: '#1d4ed8', fundo: '#eff6ff', rotulo: 'Fazendo' },
    'Parado':              { cor: '#b91c1c', fundo: '#fef2f2', rotulo: 'Parado' },
    'Concluida':           { cor: '#15803d', fundo: '#f0fdf4', rotulo: 'Concluída' },
    'Recusada':            { cor: '#57534e', fundo: '#fafaf9', rotulo: 'Recusada' },
};

// ⚠️ TODAS as colunas do quadro têm o MESMO fundo branco, e isso é decisão de
//    17/09: o fundo colorido por estado deixava o quadro sujo. O contraste que
//    funciona é o branco da coluna contra o cinza da página — quem diz o estado
//    é o título, o contador e a barra lateral do card, não uma mancha atrás.
export const FUNDO_COLUNA = '#ffffff';

// O texto padrão. Fica aqui porque é ele que manda nos botões e códigos: cor de
// estado em TEXTO longo cansa e faz tudo parecer alerta.
export const TEXTO = '#0f172a';

/** Nunca devolve undefined: estado desconhecido vira cinza legível, não quebra de layout. */
export const doStatus = (s) => STATUS[s] || { cor: '#64748b', fundo: '#f8fafc', rotulo: s || '—' };

// A prioridade saiu da barra lateral do card (onde brigava com o vermelho de
// `Parado`) e virou selo. ⚠️ Desde 18/09 as TRÊS prioridades se anunciam no card
// (pedido do Rodrigo): antes só `Alta` aparecia e média/baixa ficavam sem nada,
// o que deixava impossível distinguir "média" de "sem prioridade definida". A
// hierarquia visual continua — vermelho (Alta) grita, cinza-médio (Media) e
// cinza-claro (Baixa) só informam.
export const PRIORIDADE = {
    Alta:  { cor: '#b91c1c', fundo: '#fef2f2' },   // vermelho — urgente
    Media: { cor: '#a16207', fundo: '#fefce8' },   // amarelo — atenção
    Baixa: { cor: '#94a3b8', fundo: '#f8fafc' },   // cinza — sem pressa
};

// Cor de IDENTIDADE de cada pessoa da equipe, para bater o olho e saber de quem é
// a demanda no quadro. ⚠️ É por PESSOA, não por "eu vs. o outro": o quadro é
// compartilhado (todos veem o MESMO board), então a Julia precisa ser
// roxa em todos os PCs, nunca "ciano porque sou eu". Mapeado pelo primeiro nome —
// a equipe é fixa e conhecida; nome fora do mapa cai no cinza neutro.
// ⚠️ O selo do responsável no card usa a cor SÓLIDA (`forte`) + texto BRANCO —
//    a mesma pinta do botão de filtro ativo, por pedido do Rodrigo (18/09). O
//    `fundo` (tom claro) fica disponível para realces suaves, mas o nome no card
//    é sólido + branco.
// ⚠️ Anna Adélia entrou na equipe em 2026-11 (vinho/bordô). Mapeado pelo PRIMEIRO nome:
//    "ANNA ADELIA ..." → `anna` (sem acento, bate certo). A conta é compartilhada
//    com a Fernanda, mas quem responde demanda nesta máquina é a Anna (ID 56) —
//    ver CLAUDE.md › Contexto do projeto e da equipe.
export const CORES_PESSOA = {
    rodrigo: { forte: '#0E7490', fundo: '#cffafe' },  // ciano
    julia:   { forte: '#7E22CE', fundo: '#ede9fe' },  // roxo
    anna:    { forte: '#9F1239', fundo: '#ffe4e6' },  // vinho/bordô
};
const COR_PESSOA_NEUTRA = { forte: '#64748b', fundo: '#f1f5f9' };

/** Devolve a cor da pessoa pelo nome completo; nunca undefined. */
export const corPessoa = (nomeCompleto) => {
    const primeiro = String(nomeCompleto || '').trim().split(/\s+/)[0].toLowerCase();
    return CORES_PESSOA[primeiro] || COR_PESSOA_NEUTRA;
};

/** É alguém da equipe de dados (tem cor própria)? Usado para o filtro do quadro
 *  listar só quem toca demanda (Rodrigo, Julia, Anna) — a `/equipe` traz mais
 *  gente (Admin, outros perfis de gestão) que não interessa como botão de filtro.
 *  ⚠️ O nome `ehDupla` ficou do tempo de duas pessoas; hoje é "é da equipe". */
export const ehDupla = (nomeCompleto) => {
    const primeiro = String(nomeCompleto || '').trim().split(/\s+/)[0].toLowerCase();
    return Object.prototype.hasOwnProperty.call(CORES_PESSOA, primeiro);
};

export const STATUS_TAREFA = ['A fazer', 'Fazendo', 'Concluida', 'Cancelada'];

/** Ordem das colunas do quadro: o que está na mão primeiro. */
export const COLUNAS = ['Fazendo', 'Pendente de triagem', 'A fazer', 'Parado'];

export const TIPOS = ['feat', 'fix', 'etl', 'refactor', 'infra', 'doc', 'decisao', 'seg'];

// =====================================================================
// Persistência LOCAL do estado da tela (localStorage)
// =====================================================================
// Guarda no navegador o que é CONVENIÊNCIA de quem está usando: os filtros do
// quadro e os rascunhos começados (nova demanda, andamento, nova tarefa). Assim
// a tela "não esquece" ao navegar para os Chamados e voltar — a SPA desmonta o
// componente, e sem isso o estado se perde.
//
// ⚠️ É de propósito só o navegador de quem está usando: NÃO vai para o banco,
//    NÃO é compartilhado com a dupla, e é barato (texto curto; o localStorage
//    tem ~5 MB por site). Nunca guardar aqui nada que precise ser confiável ou
//    visto por outra pessoa — isso é dado, e mora no banco.
// ⚠️ Todo acesso vem embrulhado em try/catch: em aba anônima, ou com o
//    armazenamento bloqueado, ler/gravar PODE lançar — e a tela tem de seguir
//    inteira sem o rascunho, nunca quebrar por causa dele.
const PREFIXO_LS = 'demandas:';

/** Lê um valor JSON do localStorage; devolve `padrao` se não houver ou falhar. */
export const lerLocal = (chave, padrao) => {
    try {
        const bruto = localStorage.getItem(PREFIXO_LS + chave);
        return bruto === null ? padrao : JSON.parse(bruto);
    } catch {
        return padrao;
    }
};

/** Grava um valor JSON no localStorage. Falha silenciosa (ver aviso acima). */
export const gravarLocal = (chave, valor) => {
    try {
        localStorage.setItem(PREFIXO_LS + chave, JSON.stringify(valor));
    } catch {
        /* sem localStorage disponível: a tela segue sem persistir o rascunho */
    }
};
